/**
 * Administrator list of delivered documents (書類交付 / 一覧).
 * Search and filters follow the payslip display: name search, type, year, and clear.
 */

import React, { useEffect, useMemo, useState } from 'react';
import '../SalaryDataUploadDownload/css/DataDisplay.css';
import './css/DocumentDelivery.css';
import { DOCUMENT_TYPES } from './documentTypes';
import { compactContainsQuery } from '../../utils/personNameMatch';
import {
  EmployeeDocumentItem,
  deleteEmployeeDocument,
  downloadEmployeeDocument,
  listEmployeeDocuments,
  openEmployeeDocument,
} from '../../services/EmployeeDocumentService';

function periodLabel(document: EmployeeDocumentItem): string {
  if (document.revisionMonth > 0) {
    return `令和${document.eraYear}年${document.revisionMonth}月`;
  }
  return '';
}

export const AdminDocumentList: React.FC = () => {
  const [documents, setDocuments] = useState<EmployeeDocumentItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [openingId, setOpeningId] = useState<number | null>(null);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  useEffect(() => {
    let active = true;
    listEmployeeDocuments().then((result) => {
      if (!active) return;
      setDocuments(result.data);
      setError(result.success ? '' : result.message);
      setIsLoading(false);
    });
    return () => {
      active = false;
    };
  }, []);

  const years = useMemo(() => {
    const values = documents.map((document) => document.eraYear);
    return Array.from(new Set(values)).sort((a, b) => b - a);
  }, [documents]);

  const filtered = documents.filter((document) => {
    if (search.trim() && !compactContainsQuery(document.personName, search)) return false;
    if (typeFilter && document.documentTypeLabel !== typeFilter) return false;
    if (yearFilter && String(document.eraYear) !== yearFilter) return false;
    return true;
  });

  const clearFilters = () => {
    setSearch('');
    setTypeFilter('');
    setYearFilter('');
  };

  const preview = async (id: number) => {
    setOpeningId(id);
    setError('');
    const result = await openEmployeeDocument(id);
    if (!result.success) setError(result.message);
    setOpeningId(null);
  };

  const download = async (document: EmployeeDocumentItem) => {
    setDownloadingId(document.id);
    setError('');
    const result = await downloadEmployeeDocument(document.id, document.originalFileName);
    if (!result.success) setError(result.message);
    setDownloadingId(null);
  };

  const remove = async (id: number) => {
    if (!window.confirm('本当に削除してもよろしいですか？')) return;
    setDeletingId(id);
    setError('');
    setNotice('');
    const result = await deleteEmployeeDocument(id);
    if (result.success) {
      setDocuments((current) => current.filter((document) => document.id !== id));
      setNotice(result.message);
    } else {
      setError(result.message);
    }
    setDeletingId(null);
  };

  return (
    <div className="document-list">
      <div className="controls-section">
        <div className="filter-operations">
          <div className="filter-group">
            <label htmlFor="document-search">検索:</label>
            <input
              id="document-search"
              type="text"
              value={search}
              placeholder="氏名で検索"
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
          <div className="filter-group">
            <label htmlFor="document-type-filter">書類の種類:</label>
            <select
              id="document-type-filter"
              value={typeFilter}
              onChange={(event) => setTypeFilter(event.target.value)}
            >
              <option value="">すべて</option>
              {DOCUMENT_TYPES.map((definition) => (
                <option key={definition.id} value={definition.label}>{definition.label}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label htmlFor="document-year-filter">年:</label>
            <select
              id="document-year-filter"
              value={yearFilter}
              onChange={(event) => setYearFilter(event.target.value)}
            >
              <option value="">すべて</option>
              {years.map((year) => (
                <option key={year} value={String(year)}>{`令和${year}年`}</option>
              ))}
            </select>
          </div>
          <button type="button" className="clear-filters" onClick={clearFilters}>
            フィルタークリア
          </button>
        </div>
      </div>

      {notice && <div className="delete-success" role="status"><span>{notice}</span></div>}
      {isLoading && <div className="no-data"><p>読み込み中...</p></div>}
      {!isLoading && error && <div className="delete-error" role="alert"><span>{error}</span></div>}
      {!isLoading && !error && documents.length === 0 && (
        <div className="no-data"><p>書類はまだありません。</p></div>
      )}
      {!isLoading && !error && documents.length > 0 && filtered.length === 0 && (
        <div className="no-data"><p>条件に一致する書類はありません。</p></div>
      )}
      {!isLoading && filtered.length > 0 && (
        <div className="data-table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>書類の種類</th>
                <th>対象者</th>
                <th>年</th>
                <th>改定年月</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((document) => (
                <tr key={document.id}>
                  <td>{document.documentTypeLabel}</td>
                  <td>{document.personName}</td>
                  <td>{`令和${document.eraYear}年`}</td>
                  <td>{periodLabel(document)}</td>
                  <td>
                    <button
                      type="button"
                      className="preview-row-btn"
                      onClick={() => preview(document.id)}
                      disabled={openingId === document.id || downloadingId === document.id}
                    >
                      {openingId === document.id ? '開いています...' : 'プレビュー'}
                    </button>
                    <button
                      type="button"
                      className="preview-row-btn"
                      onClick={() => download(document)}
                      disabled={openingId === document.id || downloadingId === document.id}
                    >
                      {downloadingId === document.id ? 'ダウンロード中...' : 'ダウンロード'}
                    </button>
                    <button
                      type="button"
                      className="delete-row-btn"
                      onClick={() => remove(document.id)}
                      disabled={deletingId === document.id}
                    >
                      {deletingId === document.id ? '削除中...' : '削除'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
